import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  Tabs,
  message,
} from "antd";
import {
  FilterOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import { getZonesStatistics } from "../../../api/statistics/statisticsRequest";
import { getZones } from "../../../api/zone/zoneRequest";

// Initialize the heatmap module
HighchartsHeatmap(Highcharts);

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const ZoneStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoneData, setZoneData] = useState(null);

  const [zones, setZones] = useState([]);

  useEffect(() => {
    fetchZones();
  }, []);

  const filterZoneStatistics = async () => {
    setLoading(true);
    try {
      const data = await getZonesStatistics({
        start_date:
          dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null,
        end_date:
          dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null,
        zone_id: selectedZone,
      });
      setZoneData(data); // Update state with the fetched data
    } catch (error) {
      setZoneData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };
  const fetchZones = async () => {
    setLoading(true);
    try {
      const data = await getZones();
      setZones(data.data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des zones");
    } finally {
      setLoading(false);
    }
  };

  const hourlyInfractionChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Hourly Infraction Distribution",
    },
    xAxis: {
      categories: zoneData
        ? zoneData.hourly_analysis.hourly_breakdown.map((item) => item.hour)
        : [],
      title: {
        text: "Hour of Day",
      },
    },
    yAxis: [
      {
        title: {
          text: "Number of Infractions",
        },
      },
      {
        title: {
          text: "Average Amount",
        },
        opposite: true,
      },
    ],
    series: [
      {
        name: "Total Infractions",
        type: "column",
        data: zoneData
          ? zoneData.hourly_analysis.hourly_breakdown.map(
              (item) => item.total_infractions
            )
          : [],
      },
      {
        name: "Paid Infractions",
        type: "column",
        data: zoneData
          ? zoneData.hourly_analysis.hourly_breakdown.map(
              (item) => item.paid_infractions
            )
          : [],
      },
      {
        name: "Average Amount",
        type: "spline",
        yAxis: 1,
        data: zoneData
          ? zoneData.hourly_analysis.hourly_breakdown.map((item) =>
              parseFloat(item.average_amount)
            )
          : [],
        tooltip: {
          valuePrefix: "$",
        },
      },
    ],
  };

  const agentCoverageHeatmapOptions = {
    chart: {
      type: "heatmap",
    },
    title: {
      text: "Agent Coverage Heatmap",
    },
    xAxis: {
      categories: [
        "00:00",
        "01:00",
        "02:00",
        "03:00",
        "04:00",
        "05:00",
        "06:00",
        "07:00",
        "08:00",
        "09:00",
        "10:00",
        "11:00",
        "12:00",
        "13:00",
        "14:00",
        "15:00",
        "16:00",
        "17:00",
        "18:00",
        "19:00",
        "20:00",
        "21:00",
        "22:00",
        "23:00",
      ],
    },
    yAxis: {
      categories: ["Agents"],
      title: null,
    },
    colorAxis: {
      min: 0,
      minColor: "#FFFFFF",
      maxColor: Highcharts.getOptions().colors[0],
    },
    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      y: 25,
      symbolHeight: 280,
    },
    tooltip: {
      formatter: function () {
        return (
          "<b>" +
          this.series.xAxis.categories[this.point.x] +
          ":00</b> had <br><b>" +
          this.point.value +
          "</b> active agents"
        );
      },
    },
    series: [
      {
        name: "Active Agents per Hour",
        borderWidth: 1,
        type: "heatmap",
        data: zoneData
          ? zoneData.agent_coverage.coverage_analysis.time_coverage.hourly_coverage.map(
              (item, index) => [parseInt(item.hour), 0, item.active_agents]
            )
          : [],
      },
    ],
  };

  const agentColumns = [
    {
      title: "Agent Name",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Total Infractions",
      dataIndex: "total_infractions",
      key: "total_infractions",
      sorter: (a, b) => a.total_infractions - b.total_infractions,
    },
    {
      title: "Paid Infractions",
      dataIndex: "paid_infractions",
      key: "paid_infractions",
      sorter: (a, b) => a.paid_infractions - b.paid_infractions,
    },
    {
      title: "Days Active",
      dataIndex: "days_active",
      key: "days_active",
      sorter: (a, b) => a.days_active - b.days_active,
    },
    {
      title: "Total Revenue",
      dataIndex: "total_revenue",
      key: "total_revenue",
      sorter: (a, b) =>
        parseFloat(a.total_revenue) - parseFloat(b.total_revenue),
      render: (text) => `$${parseFloat(text).toFixed(2)}`,
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Filtrer par Contrat"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedZone(value)}
                  allowClear
                >
                  {zones.map((zone) => (
                    <Option key={zone.id} value={zone.id}>
                      {zone.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Row>
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterZoneStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Filter
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {zoneData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Zone Statistics Summary
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Active Agents"
                    value={
                      zoneData.agent_coverage.coverage_analysis
                        .total_active_agents
                    }
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Average Infractions per Agent"
                    value={zoneData.agent_coverage.coverage_analysis.average_infractions_per_agent.toFixed(
                      2
                    )}
                    prefix={<ExclamationCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Average Coverage Rate"
                    value={`${zoneData.agent_coverage.coverage_analysis.coverage_metrics.average_coverage_rate.toFixed(
                      2
                    )}%`}
                    prefix={<ClockCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Top Agent Revenue"
                    value={parseFloat(
                      zoneData.agent_coverage.coverage_analysis.agent_rankings
                        .by_revenue[0]?.total_revenue
                    ).toFixed(2)}
                    prefix={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={hourlyInfractionChartOptions}
              />
            </Card>

            <Card className="shadow-sm">
              <HighchartsReact
                highcharts={Highcharts}
                options={agentCoverageHeatmapOptions}
              />
            </Card>

            <Card className="shadow-sm">
              <Tabs defaultActiveKey="1">
                <TabPane tab="Agent Rankings" key="1">
                  <Table
                    dataSource={zoneData.agent_coverage.agent_details}
                    columns={agentColumns}
                    rowKey="id_agent"
                  />
                </TabPane>
                <TabPane tab="Coverage Gaps" key="2">
                  <Table
                    dataSource={
                      zoneData.agent_coverage.coverage_analysis.time_coverage
                        .coverage_gaps
                    }
                    columns={[
                      {
                        title: "Start Hour",
                        dataIndex: "start_hour",
                        key: "start_hour",
                      },
                      {
                        title: "End Hour",
                        dataIndex: "end_hour",
                        key: "end_hour",
                      },
                      {
                        title: "Duration (hours)",
                        dataIndex: "duration",
                        key: "duration",
                      },
                    ]}
                    rowKey={(record) =>
                      `${record.start_hour}-${record.end_hour}`
                    }
                  />
                </TabPane>
              </Tabs>
            </Card>

            <Card className="shadow-sm">
              <Title level={3} className="mb-4">
                Period Analysis
              </Title>
              <Table
                dataSource={Object.entries(
                  zoneData.hourly_analysis.period_analysis
                ).map(([key, value]) => ({
                  key,
                  period: key.charAt(0).toUpperCase() + key.slice(1),
                  ...value,
                }))}
                columns={[
                  { title: "Period", dataIndex: "period", key: "period" },
                  {
                    title: "Total Infractions",
                    dataIndex: "total_infractions",
                    key: "total_infractions",
                  },
                  {
                    title: "Paid Infractions",
                    dataIndex: "paid_infractions",
                    key: "paid_infractions",
                  },
                  {
                    title: "Average Amount",
                    dataIndex: "average_amount",
                    key: "average_amount",
                    render: (text) => `$${text.toFixed(2)}`,
                  },
                  {
                    title: "Unique Agents",
                    dataIndex: "unique_agents",
                    key: "unique_agents",
                  },
                  {
                    title: "Peak Hour",
                    dataIndex: "peak_hour",
                    key: "peak_hour",
                  },
                  {
                    title: "Payment Rate",
                    dataIndex: "payment_rate",
                    key: "payment_rate",
                    render: (text) => `${text.toFixed(2)}%`,
                  },
                ]}
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default ZoneStatistics;
